import { Controller } from "@hotwired/stimulus"
import { LAND, landPoints } from "../passport_map/land"
import { RAD, naturalEarth, greatCircle } from "../passport_map/geo"

const DAY = 86400
// A fresh stamp ripples at the passport for this long.
const STAMP_FLASH = 2 * DAY
// The whole timeline plays in this many seconds at 1×.
const PLAY_SECONDS = 60
const LEG_STEPS = 48
const RUBY = "225, 29, 72"
const PALETTE = [ "#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#3b82f6", "#8b5cf6", "#ec4899", "#e11d48", "#0ea5e9" ]
const FONT = "ui-rounded, 'SF Pro Rounded', ui-sans-serif, system-ui, sans-serif"

const dateFormat = new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric" })
const monthFormat = new Intl.DateTimeFormat(undefined, { month: "short" })

// Passports traveling between Ruby events on a dotted world map, played back on a timeline.
export default class extends Controller {
  static targets = [ "stage", "land", "map", "timeline", "date", "counters", "mode", "top", "topWrap", "topLabel",
    "visited", "search", "ids", "trails", "itinerary", "speed", "playIcon", "pauseIcon", "hint", "controls" ]
  static values = { journeys: Object }

  connect() {
    this.prepare()
    this.populateControls()
    this.filter()

    this.time = this.start
    this.playing = !window.matchMedia("(prefers-reduced-motion: reduce)").matches && this.passports.length > 0
    this.showPlaying()

    this.onWheel = this.wheel.bind(this)
    this.mapTarget.addEventListener("wheel", this.onWheel, { passive: false })
    this.resizeObserver = new ResizeObserver(() => this.resize())
    this.resizeObserver.observe(this.stageTarget)
    this.resizeObserver.observe(this.timelineTarget)

    this.tick = this.tick.bind(this)
    this.frame = requestAnimationFrame(this.tick)
  }

  disconnect() {
    cancelAnimationFrame(this.frame)
    this.resizeObserver.disconnect()
    this.mapTarget.removeEventListener("wheel", this.onWheel)
  }

  // Data

  prepare() {
    const { events, passports } = this.journeysValue
    this.events = events.map((event, i) => ({ ...event, i, times: [] }))
    this.legs = new Map()
    this.passports = passports.map((passport) => {
      const stamps = passport.stamps.map(([e, t]) => ({ e, t }))
      stamps.forEach(({ e, t }) => this.events[e].times.push(t))
      const legs = stamps.slice(1).map((stamp, i) => this.leg(stamps[i].e, stamp.e))
      const [ h1, h2 ] = hash(passport.id)
      const trips = stamps.slice(1).map((next, i) => plan(stamps[i].t, next.t, hash(`${passport.id}:${i}`), hash(`${passport.id}:${i}:bow`)))
      return { id: passport.id, stamps, legs, trips, spread: Math.sqrt(h1), angle: h2 * Math.PI * 2, ownColor: PALETTE[Math.floor(h2 * 997) % PALETTE.length] }
    })

    const times = this.events.flatMap((event) => event.times).sort((a, b) => a - b)
    this.events.forEach((event) => {
      event.times.sort((a, b) => a - b)
      event.first = event.times[0]
      event.last = event.times[event.times.length - 1]
    })
    this.stampLongitude = this.events.reduce((sum, event) => sum + event.lng * event.times.length, 0) / Math.max(1, times.length)
    const now = Date.now() / 1000
    this.start = (times[0] ?? now) - 5 * DAY
    this.end = (times[times.length - 1] ?? now) + 5 * DAY

    this.histogram = new Map()
    times.forEach((t) => {
      const day = Math.floor(t / DAY)
      this.histogram.set(day, (this.histogram.get(day) || 0) + 1)
    })
    this.histogramMax = Math.max(1, ...this.histogram.values())

    this.landPoints = landPoints()
  }

  leg(from, to) {
    const key = `${from}>${to}`
    if (!this.legs.has(key)) {
      const a = this.events[from]
      const b = this.events[to]
      this.legs.set(key, { from, to, path: greatCircle([ a.lng, a.lat ], [ b.lng, b.lat ]), points: null })
    }
    return this.legs.get(key)
  }

  populateControls() {
    const byDate = [ ...this.events ].filter((event) => event.first).sort((a, b) => a.first - b.first)
    byDate.forEach((event) => this.visitedTarget.add(new Option(`${event.name} · ${event.city || ""}`, event.i)))
    this.passports.forEach((passport) => this.idsTarget.append(new Option(passport.id)))
    this.topTarget.max = Math.max(1, Math.min(50, this.passports.length))
    this.topTarget.value = Math.min(10, this.passports.length)
  }

  // Controls

  filter() {
    const mode = this.modeTarget.value
    const visited = this.visitedTarget.value === "" ? null : Number(this.visitedTarget.value)
    let shown = this.passports.filter((passport) => visited === null || passport.stamps.some((stamp) => stamp.e === visited))

    this.topWrapTarget.classList.toggle("hidden", mode !== "top")
    this.topWrapTarget.classList.toggle("block", mode === "top")
    if (mode === "top") {
      const count = Number(this.topTarget.value)
      this.topLabelTarget.textContent = count
      shown = [ ...shown ].sort((a, b) => b.stamps.length - a.stamps.length || a.stamps[0].t - b.stamps[0].t).slice(0, count)
    }
    // Everyone gets a color of their own; the biggest travelers get distinct ones by rank.
    shown.forEach((passport, i) => { passport.color = mode === "top" ? PALETTE[i % PALETTE.length] : passport.ownColor })
    this.shown = shown
    this.showTrails = this.trailsTarget.checked
    this.filterVersion = (this.filterVersion || 0) + 1
    this.stampTimes = shown.flatMap((passport) => passport.stamps.map((stamp) => stamp.t)).sort((a, b) => a - b)
  }

  search() {
    const id = this.searchTarget.value.trim().toLowerCase()
    // Public passports are named "Passport 12", so a bare number finds one too.
    const passport = this.passports.find((passport) => [ id, `passport ${id}` ].includes(passport.id.toLowerCase()))
    this.focus(passport || null, false)
  }

  focus(passport, fillSearch = true) {
    this.focused = passport
    if (fillSearch) this.searchTarget.value = passport ? passport.id : ""
    this.itineraryTarget.classList.toggle("hidden", !passport)
    this.itineraryTarget.replaceChildren(...(passport ? passport.stamps.map((stamp) => {
      const event = this.events[stamp.e]
      const item = document.createElement("li")
      item.className = "cursor-pointer rounded-lg px-2 py-1 hover:bg-stone-100"
      item.dataset.time = stamp.t
      item.innerHTML = `<div class="font-semibold text-stone-800"></div><div class="text-xs text-stone-500"></div>`
      item.firstChild.textContent = event.name
      item.lastChild.textContent = `${event.city || ""} · ${dateFormat.format(stamp.t * 1000)}`
      item.addEventListener("click", () => { this.time = stamp.t; this.pause() })
      return item
    }) : []))
  }

  toggle() {
    if (this.playing) return this.pause()
    if (this.time >= this.end) this.time = this.start
    this.playing = true
    this.showPlaying()
  }

  pause() {
    this.playing = false
    this.showPlaying()
  }

  showPlaying() {
    this.playIconTarget.classList.toggle("hidden", this.playing)
    this.pauseIconTarget.classList.toggle("hidden", !this.playing)
  }

  keydown(event) {
    if (event.target.closest("input, select, textarea")) return
    if (event.key === " ") {
      event.preventDefault()
      this.toggle()
    }
  }

  resetView() {
    this.view = this.fit()
    this.viewChanged()
  }

  // Map interaction

  // One pointer drags the map; two pinch to zoom around the point between the fingers.
  pointerdown(event) {
    this.mapTarget.setPointerCapture(event.pointerId)
    this.touches ||= new Map()
    this.touches.set(event.pointerId, [ event.offsetX, event.offsetY ])
    this.startGesture()
  }

  pointermove(event) {
    this.pointer = event.pointerType === "mouse" ? [ event.offsetX, event.offsetY ] : null
    if (!this.drag || !this.touches.has(event.pointerId)) return
    this.touches.set(event.pointerId, [ event.offsetX, event.offsetY ])
    const { center, spread } = this.gesture()
    if (Math.hypot(center[0] - this.drag.center[0], center[1] - this.drag.center[1]) > 4 || this.touches.size > 1) this.drag.moved = true
    if (!this.drag.moved) return

    const scale = this.clampScale(this.drag.view.scale * (this.drag.spread ? spread / this.drag.spread : 1))
    const f = scale / this.drag.view.scale
    this.view = {
      scale,
      ox: center[0] - (this.drag.center[0] - this.drag.view.ox) * f,
      oy: center[1] - (this.drag.center[1] - this.drag.view.oy) * f
    }
    this.viewChanged()
    this.hintTarget.classList.add("hidden")
  }

  pointerup(event) {
    if (!this.touches?.delete(event.pointerId)) return
    if (this.drag && !this.drag.moved && this.touches.size === 0) {
      this.focus(this.hit(event.offsetX, event.offsetY, event.pointerType === "mouse" ? 8 : 20))
    }
    // Lifting one finger of a pinch carries on as a drag, without counting as a tap.
    const moved = this.drag?.moved
    this.startGesture()
    if (this.drag) this.drag.moved = moved
  }

  pointerleave() {
    this.pointer = null
  }

  startGesture() {
    this.drag = this.touches.size ? { ...this.gesture(), view: this.view, moved: false } : null
  }

  gesture() {
    const points = [ ...this.touches.values() ]
    const center = [ points.reduce((sum, [ x ]) => sum + x, 0) / points.length, points.reduce((sum, [ , y ]) => sum + y, 0) / points.length ]
    const spread = points.length > 1 ? Math.hypot(points[0][0] - points[1][0], points[0][1] - points[1][1]) : 0
    return { center, spread }
  }

  clampScale(scale) {
    return Math.min(this.worldScale * 40, Math.max(this.worldScale * 0.8, scale))
  }

  wheel(event) {
    event.preventDefault()
    const scale = this.clampScale(this.view.scale * Math.exp(-event.deltaY * 0.0015))
    const f = scale / this.view.scale
    this.view = { scale, ox: event.offsetX - (event.offsetX - this.view.ox) * f, oy: event.offsetY - (event.offsetY - this.view.oy) * f }
    this.viewChanged()
    this.hintTarget.classList.add("hidden")
  }

  toggleControls() {
    this.controlsTarget.classList.toggle("max-sm:hidden")
  }

  hit(x, y, radius = 8) {
    let best = null
    let bestDistance = radius ** 2
    this.positions?.forEach(({ passport, x: px, y: py }) => {
      const distance = (px - x) ** 2 + (py - y) ** 2
      if (distance < bestDistance) {
        best = passport
        bestDistance = distance
      }
    })
    return best
  }

  // Timeline interaction

  scrubStart(event) {
    this.timelineTarget.setPointerCapture(event.pointerId)
    this.scrubbing = true
    this.wasPlaying = this.playing
    this.pause()
    this.scrubMove(event)
  }

  scrubMove(event) {
    this.timelineHover = event.offsetX
    if (this.scrubbing) this.time = this.timeAt(event.offsetX)
  }

  scrubEnd() {
    this.scrubbing = false
    if (this.wasPlaying && this.time < this.end) {
      this.playing = true
      this.showPlaying()
    }
  }

  timelineLeave() {
    this.timelineHover = null
  }

  // Layout

  resize() {
    const dpr = window.devicePixelRatio || 1
    for (const canvas of [ this.landTarget, this.mapTarget, this.timelineTarget ]) {
      const { width, height } = canvas.getBoundingClientRect()
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      canvas.getContext("2d").setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    this.width = this.mapTarget.clientWidth
    this.height = this.mapTarget.clientHeight
    this.trailLayer ||= document.createElement("canvas")
    this.trailLayer.width = this.mapTarget.width
    this.trailLayer.height = this.mapTarget.height
    this.trailLayer.getContext("2d").setTransform(dpr, 0, 0, dpr, 0, 0)
    this.base = this.fit()
    this.view = this.base
    this.viewChanged()
  }

  // Fits the world from 84°N to 57°S, leaving room for the date in the top left.
  fit() {
    const [ right ] = naturalEarth(Math.PI, 0)
    const [ , top ] = naturalEarth(0, 84 * RAD)
    const [ , bottom ] = naturalEarth(0, -57 * RAD)
    const padding = { top: this.width < 640 ? 124 : 184, right: 16, bottom: 16, left: 16 }
    const width = this.width - padding.left - padding.right
    const height = this.height - padding.top - padding.bottom
    this.worldScale = Math.min(width / (2 * right), height / (top - bottom))
    let scale = this.worldScale
    let ox = padding.left + width / 2

    // A whole world is a thin strip on a portrait phone, so zoom in towards where most stamps
    // happen, without panning past the edges of the map.
    if (width < height) {
      scale = Math.min(height / (top - bottom), this.worldScale * 2.5)
      const [ center ] = naturalEarth(this.stampLongitude * RAD, 0)
      ox = padding.left + width / 2 - center * scale
      ox = Math.min(padding.left + right * scale, Math.max(padding.left + width - right * scale, ox))
    }

    return { scale, ox, oy: padding.top + (height - (top - bottom) * scale) / 2 + top * scale }
  }

  project([ lng, lat ]) {
    const [ x, y ] = naturalEarth(lng * RAD, lat * RAD)
    return [ this.view.ox + x * this.view.scale, this.view.oy - y * this.view.scale ]
  }

  viewChanged() {
    this.legs.forEach((leg) => { leg.points = null })
    this.passports.forEach((passport) => { passport.points = null })
    this.viewVersion = (this.viewVersion || 0) + 1
    this.events.forEach((event) => { [ event.x, event.y ] = this.project([ event.lng, event.lat ]) })
    this.drawLand()
  }

  // Screen points along a route, with null where it wraps around the edge of the map.
  legPoints(leg) {
    if (!leg.points) {
      const halfWidth = Math.PI * 0.87 * this.view.scale
      leg.points = []
      let previous = null
      for (let i = 0; i <= LEG_STEPS; i++) {
        const point = this.project(leg.path(i / LEG_STEPS))
        if (previous && Math.abs(point[0] - previous[0]) > halfWidth) leg.points.push(null)
        leg.points.push(point)
        previous = point
      }
    }
    return leg.points
  }

  // Drawing

  drawLand() {
    const ctx = this.landTarget.getContext("2d")
    ctx.clearRect(0, 0, this.width, this.height)
    const spacing = LAND.step * RAD * 0.87 * this.view.scale
    const size = Math.max(0.8, Math.min(spacing * 0.5, 4))
    ctx.fillStyle = "#ddd0b6"
    for (const point of this.landPoints) {
      const [ x, y ] = this.project(point)
      if (x < -size || y < -size || x > this.width + size || y > this.height + size) continue
      ctx.beginPath()
      ctx.arc(x, y, size / 2, 0, Math.PI * 2)
      ctx.fill()
    }
  }

  tick(now) {
    const elapsed = this.lastFrame ? Math.min(0.1, (now - this.lastFrame) / 1000) : 0
    this.lastFrame = now
    if (this.playing) {
      this.time += elapsed * ((this.end - this.start) / PLAY_SECONDS) * Number(this.speedTarget.value)
      if (this.time >= this.end) {
        this.time = this.end
        this.pause()
      }
    }
    if (this.width) {
      this.drawMap(now / 1000)
      this.drawTimeline()
      this.drawLabels()
    }
    this.frame = requestAnimationFrame(this.tick)
  }

  // Where a passport is at time t: at the event it was last stamped at, on its way to the next
  // one, or already there and waiting for the stamp. done counts the trips it has finished.
  locate(passport, t) {
    const stamps = passport.stamps
    if (!stamps.length || t < stamps[0].t) return null
    let i = 0
    while (i + 1 < stamps.length && stamps[i + 1].t <= t) i++
    const state = { passport, i, flying: false, event: stamps[i].e, stampedAt: stamps[i].t, done: i }
    const trip = passport.trips[i]
    if (trip && t >= trip.arrive) return { ...state, event: stamps[i + 1].e, done: i + 1 }
    if (trip && t > trip.depart) return { ...state, flying: true, u: ease((t - trip.depart) / (trip.arrive - trip.depart)) }
    return state
  }

  // Screen points of one passport's trip, bowed to its own side of the route so passports
  // flying between the same cities spread out instead of sharing one line.
  tripPoints(passport, i) {
    passport.points ||= []
    if (!passport.points[i]) {
      const base = this.legPoints(passport.legs[i])
      const first = base[0]
      const last = base[base.length - 1]
      if (base.includes(null)) {
        passport.points[i] = base
      } else {
        const bow = passport.trips[i].bow
        const nx = -(last[1] - first[1])
        const ny = last[0] - first[0]
        passport.points[i] = base.map(([ x, y ], k) => {
          const lift = bow * Math.sin((k / (base.length - 1)) * Math.PI)
          return [ x + nx * lift, y + ny * lift ]
        })
      }
    }
    return passport.points[i]
  }

  pointAlong(points, u) {
    const position = u * (points.length - 1)
    const index = Math.floor(position)
    const a = points[index] || points[index - 1]
    const b = points[index + 1] || a
    if (!a) return points[points.length - 1]
    if (!b) return a
    const f = position - index
    return [ a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f ]
  }

  drawMap(clock) {
    const ctx = this.mapTarget.getContext("2d")
    const t = this.time
    ctx.clearRect(0, 0, this.width, this.height)

    const states = this.shown.map((passport) => this.locate(passport, t)).filter(Boolean)
    const resting = new Map()
    states.forEach((state) => { if (!state.flying) resting.set(state.event, (resting.get(state.event) || 0) + 1) })

    const focused = this.focused
    const dim = focused ? 0.2 : 1
    const live = this.liveAt(t)

    // Finished trips change rarely, so they are drawn once into a cached layer.
    if (this.showTrails) {
      const done = states.reduce((sum, state) => sum + state.done, 0)
      const key = `${this.viewVersion}|${this.filterVersion}|${focused?.id}|${done}|${focused ? this.locate(focused, t)?.done : ""}`
      if (key !== this.trailKey) {
        this.trailKey = key
        this.drawTrails(focused ? [ this.locate(focused, t) ].filter(Boolean) : states, focused)
      }
      ctx.drawImage(this.trailLayer, 0, 0, this.width, this.height)

      ctx.lineCap = "round"
      ctx.setLineDash([ 0.1, 5 ])
      states.forEach((state) => {
        if (!state.flying || (focused && state.passport !== focused)) return
        ctx.strokeStyle = state.passport.color
        ctx.globalAlpha = focused || this.modeTarget.value === "top" ? 0.9 : 0.55
        ctx.lineWidth = focused ? 3 : 2
        this.strokePoints(ctx, this.tripPoints(state.passport, state.i), state.u)
      })
      ctx.globalAlpha = 1
      ctx.setLineDash([])
    }

    // Cities
    this.events.forEach((event) => {
      if (!event.first) return
      const isLive = live.includes(event)
      const count = bisect(event.times, t)
      if (isLive) {
        // A rubber stamp turning slowly over the city while the event is on.
        ctx.save()
        ctx.translate(event.x, event.y)
        ctx.rotate(clock * 0.6)
        ctx.strokeStyle = "rgba(225, 29, 72, 0.55)"
        ctx.lineWidth = 2
        ctx.setLineDash([ 4, 4 ])
        ctx.beginPath()
        ctx.arc(0, 0, 17 + Math.sin(clock * 3) * 1.5, 0, Math.PI * 2)
        ctx.stroke()
        ctx.restore()
        ctx.setLineDash([])
      }
      ctx.fillStyle = "#ffffff"
      ctx.strokeStyle = isLive || count ? "#e11d48" : "#cbbfa8"
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.arc(event.x, event.y, isLive ? 6 : 4.5, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()
    })

    // Passports
    const seen = new Map()
    this.positions = states.map((state) => {
      const { passport } = state
      let x, y
      if (state.flying) {
        [ x, y ] = this.pointAlong(this.tripPoints(passport, state.i), state.u)
      } else {
        const event = this.events[state.event]
        const radius = clusterRadius(resting.get(state.event)) * passport.spread
        const angle = passport.angle + Math.sin(clock * 0.7 + passport.angle * 3) * 0.12
        x = event.x + Math.cos(angle) * radius
        y = event.y + Math.sin(angle) * radius
      }
      seen.set(passport, [ x, y ])
      return { passport, x, y, state }
    })

    this.positions.forEach(({ passport, x, y, state }) => {
      const isFocused = passport === focused
      ctx.globalAlpha = isFocused ? 1 : dim
      ctx.fillStyle = passport.color
      ctx.strokeStyle = "#ffffff"
      ctx.lineWidth = state.flying ? 1.5 : 1
      ctx.beginPath()
      ctx.arc(x, y, isFocused ? 6 : state.flying ? 3.6 : 2.8, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()

      const age = t - state.stampedAt
      if (!state.flying && age < STAMP_FLASH) {
        const f = age / STAMP_FLASH
        ctx.globalAlpha = (isFocused ? 1 : dim) * (1 - f)
        ctx.strokeStyle = passport.color
        ctx.lineWidth = 1.5
        ctx.beginPath()
        ctx.arc(x, y, 3 + f * 12, 0, Math.PI * 2)
        ctx.stroke()
      }
    })
    ctx.globalAlpha = 1

    // Labels: the followed passport and live events first, then city names where they fit.
    const placed = []
    if (focused && seen.has(focused)) {
      const [ x, y ] = seen.get(focused)
      ctx.strokeStyle = "#292524"
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.arc(x, y, 10, 0, Math.PI * 2)
      ctx.stroke()
      pill(ctx, [ focused.id ], x + 14, y - 14, placed, focused.color)
    }
    const labeled = new Set()
    live.forEach((event) => {
      const count = bisect(event.times, t)
      pill(ctx, [ event.name, `${event.city || ""} · ${count} ${count === 1 ? "stamp" : "stamps"}` ], event.x + 22, event.y - 16, placed)
      labeled.add(event.city)
    })
    if (this.view.scale > this.worldScale * 1.6) {
      this.events.forEach((event) => {
        if (!event.first || labeled.has(event.city)) return
        if (label(ctx, event.city || event.name, event.x + 8, event.y - 8, placed)) labeled.add(event.city)
      })
    }

    const hovered = this.pointer && !this.drag && this.hit(...this.pointer)
    this.mapTarget.style.cursor = this.drag?.moved ? "grabbing" : hovered ? "pointer" : "grab"
    if (hovered && hovered !== focused) {
      const [ x, y ] = seen.get(hovered)
      pill(ctx, [ hovered.id ], x + 10, y - 12, [], hovered.color)
    }

    this.counts = { stamped: states.length, flying: states.filter((s) => s.flying).length }
  }

  drawTrails(states, focused) {
    const ctx = this.trailLayer.getContext("2d")
    ctx.clearRect(0, 0, this.width, this.height)
    ctx.lineCap = "round"
    const strong = focused || this.modeTarget.value === "top"
    states.forEach(({ passport, done }) => {
      ctx.strokeStyle = passport.color
      ctx.lineWidth = focused ? 3 : strong ? 2.2 : 1.6
      ctx.globalAlpha = strong ? 0.85 : 0.35
      ctx.setLineDash([ 0.1, 5 ])
      for (let i = 0; i < done; i++) this.strokePoints(ctx, this.tripPoints(passport, i), 1)
      if (focused) {
        // Where the followed passport is headed next.
        ctx.globalAlpha = 0.25
        ctx.setLineDash([ 6, 6 ])
        for (let i = done; i < passport.legs.length; i++) this.strokePoints(ctx, this.tripPoints(passport, i), 1)
      }
    })
    ctx.globalAlpha = 1
    ctx.setLineDash([])
  }

  strokePoints(ctx, points, until) {
    const last = until * (points.length - 1)
    ctx.beginPath()
    let pen = false
    for (let i = 0; i < points.length && i <= last; i++) {
      const point = points[i]
      if (!point) {
        pen = false
        continue
      }
      pen ? ctx.lineTo(...point) : ctx.moveTo(...point)
      pen = true
    }
    if (until < 1 && pen) ctx.lineTo(...this.pointAlong(points, until))
    ctx.stroke()
  }

  drawLabels() {
    const date = dateFormat.format(this.time * 1000)
    if (this.dateTarget.textContent !== date) this.dateTarget.textContent = date
    const stamps = bisect(this.stampTimes, this.time)
    const counters = `${this.counts.stamped} passports · ${stamps} stamps · ${this.counts.flying} on their way`
    if (this.countersTarget.textContent !== counters) this.countersTarget.textContent = counters

    if (this.focused) {
      const state = this.locate(this.focused, this.time)
      ;[ ...this.itineraryTarget.children ].forEach((item, i) => {
        item.classList.toggle("bg-rose-50", state?.i === i && !state.flying)
        item.classList.toggle("opacity-50", !state || i > state.i)
      })
    }
  }

  // Events being stamped around t: any stamp within a day either side. Not first to last stamp,
  // because meetups stamp every month and a late scan can come a year after a conference.
  liveAt(t) {
    return this.events.filter((event) => bisect(event.times, t + DAY) > bisect(event.times, t - DAY))
  }

  timeAt(x) {
    const width = this.timelineTarget.clientWidth
    return this.start + Math.min(1, Math.max(0, x / width)) * (this.end - this.start)
  }

  xAt(t) {
    return ((t - this.start) / (this.end - this.start)) * this.timelineTarget.clientWidth
  }

  drawTimeline() {
    const canvas = this.timelineTarget
    const ctx = canvas.getContext("2d")
    const width = canvas.clientWidth
    const height = canvas.clientHeight
    const baseline = height - 22
    ctx.clearRect(0, 0, width, height)

    // Stamps per day
    const barWidth = Math.max(1, (DAY / (this.end - this.start)) * width)
    this.histogram.forEach((count, day) => {
      const x = this.xAt(day * DAY)
      const barHeight = Math.max(2, (Math.sqrt(count) / Math.sqrt(this.histogramMax)) * 34)
      ctx.fillStyle = day * DAY <= this.time ? `rgba(${RUBY}, 0.85)` : "#e7e0d3"
      ctx.fillRect(x, baseline - barHeight, barWidth, barHeight)
    })

    ctx.fillStyle = "#e7e5e4"
    ctx.fillRect(0, baseline, width, 1)

    // Months
    ctx.font = `11px ${FONT}`
    ctx.textBaseline = "top"
    const month = new Date(this.start * 1000)
    month.setDate(1)
    month.setHours(0, 0, 0, 0)
    let labelEnd = -Infinity
    while (month.getTime() / 1000 <= this.end) {
      const x = this.xAt(month.getTime() / 1000)
      if (x >= 0) {
        ctx.fillStyle = "#d6d3d1"
        ctx.fillRect(x, baseline, 1, 5)
        const text = month.getMonth() === 0 || labelEnd === -Infinity ? `${monthFormat.format(month)} ${month.getFullYear()}` : monthFormat.format(month)
        if (x > labelEnd + 8) {
          ctx.fillStyle = "#a8a29e"
          ctx.fillText(text, x + 3, baseline + 7)
          labelEnd = x + 3 + ctx.measureText(text).width
        }
      }
      month.setMonth(month.getMonth() + 1)
    }

    // The event under the pointer, or the one being stamped right now.
    const hoverTime = this.timelineHover != null ? this.timeAt(this.timelineHover) : null
    const at = hoverTime != null && this.liveAt(hoverTime).length ? hoverTime : this.time
    const named = this.liveAt(at)
    if (named.length) {
      const text = named.map((event) => event.name).join(" · ")
      ctx.font = `700 12px ${FONT}`
      ctx.textBaseline = "top"
      const x = Math.min(width - ctx.measureText(text).width - 4, Math.max(4, this.xAt(at) - 20))
      ctx.fillStyle = "#44403c"
      ctx.fillText(text, x, 2)
    }

    // Playhead
    const x = this.xAt(this.time)
    ctx.fillStyle = `rgb(${RUBY})`
    ctx.fillRect(x - 1, 18, 2, baseline - 18)
    ctx.strokeStyle = "#ffffff"
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.arc(x, baseline, 6, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
  }
}

function clusterRadius(count) {
  return 3 + Math.sqrt(count) * 1.6
}

function ease(u) {
  return u < 0.5 ? 2 * u * u : 1 - (-2 * u + 2) ** 2 / 2
}

// How many of the sorted values are at or before t.
function bisect(values, t) {
  let low = 0
  let high = values.length
  while (low < high) {
    const mid = (low + high) >> 1
    if (values[mid] <= t) low = mid + 1
    else high = mid
  }
  return low
}

// Two stable numbers in 0..1 for a passport, so it always sits in the same spot of a crowd.
function hash(text) {
  let h = 2166136261
  for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 16777619)
  const a = (h >>> 0) / 4294967296
  h = Math.imul(h ^ (h >>> 15), 2246822507)
  h = Math.imul(h ^ (h >>> 13), 3266489909)
  return [ a, ((h ^ (h >>> 16)) >>> 0) / 4294967296 ]
}

// When a passport travels between two stamps. Each trip has its own pace from the passport's
// random numbers: it leaves up to a month early, takes two to eight days on the way, often
// arrives a day or so before its stamp, and bows to its own side of the route.
function plan(from, to, [ r1, r2 ], [ r3, r4 ]) {
  const gap = to - from
  const stay = Math.min(DAY, gap * 0.25)
  const travel = Math.min((2 + r1 * 6) * DAY, (gap - stay) * 0.6)
  const arrive = to - Math.min(r2 * 1.5 * DAY, (gap - stay - travel) * 0.5)
  const latest = arrive - travel
  const depart = latest - Math.min(latest - (from + stay), r3 ** 1.5 * 30 * DAY)
  return { depart, arrive, bow: (r4 - 0.5) * 0.35 }
}

// City names: skipped off screen or where they would cover another label.
function label(ctx, text, x, y, placed) {
  ctx.font = `600 11px ${FONT}`
  ctx.textBaseline = "middle"
  const width = ctx.measureText(text).width
  const box = { left: x - 2, right: x + width + 2, top: y - 8, bottom: y + 8 }
  if (!onScreen(ctx, box) || overlaps(box, placed)) return false
  placed.push(box)
  ctx.lineWidth = 3
  ctx.strokeStyle = "rgba(251, 246, 236, 0.95)"
  ctx.strokeText(text, x, y)
  ctx.fillStyle = "#8a7f6c"
  ctx.fillText(text, x, y)
  return true
}

// A white rounded card with a bold first line, for live events and passports. A color adds a
// dot in front, matching the passport. Pulled back on screen near the right edge.
function pill(ctx, lines, x, y, placed, color = null) {
  if (x < 0 || y < 0 || x > ctx.canvas.clientWidth || y > ctx.canvas.clientHeight) return
  const fonts = [ `700 13px ${FONT}`, `500 12px ${FONT}` ]
  const widths = lines.map((line, i) => { ctx.font = fonts[i]; return ctx.measureText(line).width })
  const dot = color ? 14 : 0
  const width = Math.max(...widths) + 20 + dot
  const height = 10 + lines.length * 17
  x = Math.min(x, ctx.canvas.clientWidth - width - 6)
  const top = y - height / 2
  placed.push({ left: x, right: x + width, top, bottom: top + height })

  ctx.save()
  ctx.shadowColor = "rgba(68, 64, 60, 0.18)"
  ctx.shadowBlur = 12
  ctx.shadowOffsetY = 3
  ctx.fillStyle = "#ffffff"
  ctx.beginPath()
  ctx.roundRect(x, top, width, height, 10)
  ctx.fill()
  ctx.restore()

  if (color) {
    ctx.fillStyle = color
    ctx.beginPath()
    ctx.arc(x + 14, top + height / 2, 4, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.textBaseline = "middle"
  lines.forEach((line, i) => {
    ctx.font = fonts[i]
    ctx.fillStyle = i === 0 ? "#292524" : "#a8a29e"
    ctx.fillText(line, x + 10 + dot, top + 5 + 17 * i + 8.5)
  })
}

function onScreen(ctx, box) {
  return box.left >= 0 && box.top >= 0 && box.right <= ctx.canvas.clientWidth && box.bottom <= ctx.canvas.clientHeight
}

function overlaps(box, placed) {
  return placed.some((other) => box.left < other.right && box.right > other.left && box.top < other.bottom && box.bottom > other.top)
}
